import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  devicesAPI,
  depotsAPI,
  auditLogsAPI,
} from '../services/api';
import { useAuthStore } from '../store/authStore';
import './OperadorDashboard.css';

export default function OperadorDashboard() {
  const user = useAuthStore((state) => state.user);
  const userDepotId = user?.depot_id;

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  const [metrics, setMetrics] = useState({
    availableSeals: 0,
    assignedToday: 0,
    assignedLast7Days: 0,
    percentageInUse: 0,
  });

  const [depot, setDepot] = useState(null);
  const [devices, setDevices] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load initial dashboard data once on mount (no auto-refresh)
    fetchInitialData();
  }, []);

  useEffect(() => {
    calculateMetrics();
  }, [devices, auditLogs, filters]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      console.log('Operador user:', user);
      console.log('User depot_id:', userDepotId);
      
      const results = await Promise.allSettled([
        devicesAPI.getAll(),
        depotsAPI.getAll(),
        auditLogsAPI.getAll({}),
      ]);

      const [devicesRes, depotsRes, auditRes] = results;

      const devicesList = devicesRes.status === 'fulfilled' ? devicesRes.value.data : [];
      const depotsList = depotsRes.status === 'fulfilled' ? depotsRes.value.data : [];
      const auditLogs = auditRes.status === 'fulfilled' ? auditRes.value.data : [];

      console.log('Available depots:', depotsList);
      console.log('All devices:', devicesList);

      // Filter devices to only show those in user's depot
      const filteredDevices = devicesList.filter(d => d.depot_id === userDepotId);
      
      console.log('Filtered devices (matching depot):', filteredDevices);

      setDevices(filteredDevices);

      // Get depot info
      const userDepot = depotsList.find(d => d.id === userDepotId);
      console.log('User depot info:', userDepot);
      setDepot(userDepot);
      
      // Filter audit logs for container_number field changes
      // Parse the changes JSON and look for container_number updates
      const containerAssignmentLogs = auditLogs.filter(log => {
        // Only look at Device UPDATEs (capitalized as per backend)
        if (log.entity_type !== 'Device' || log.action !== 'UPDATE') {
          return false;
        }
        
        // Only include logs for devices in this depot
        if (!filteredDevices.some(d => d.id === log.entity_id)) {
          return false;
        }
        
        // Parse changes JSON if it exists
        try {
          if (typeof log.changes === 'string') {
            const changesObj = JSON.parse(log.changes);
            return changesObj.hasOwnProperty('container_number');
          } else if (typeof log.changes === 'object') {
            return log.changes?.hasOwnProperty('container_number');
          }
        } catch (e) {
          console.error('Error parsing changes JSON:', e, log.changes);
        }
        return false;
      });
      
      setAuditLogs(containerAssignmentLogs);
      console.log('Container assignment audit logs:', containerAssignmentLogs);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    let filteredDevices = [...devices];

    // Apply date filters only to "available seals" and "percentage in use"
    let dateFilteredDevices = [...devices];
    if (filters.startDate) {
      dateFilteredDevices = dateFilteredDevices.filter(d => {
        const createdDate = new Date(d.created_at || d.created_date);
        return createdDate >= new Date(filters.startDate);
      });
    }
    if (filters.endDate) {
      dateFilteredDevices = dateFilteredDevices.filter(d => {
        const createdDate = new Date(d.created_at || d.created_date);
        return createdDate <= new Date(filters.endDate);
      });
    }

    // Available seals: devices without container_number, within date range
    const availableSeals = dateFilteredDevices.filter(
      d => !d.container_number || d.container_number.trim() === ''
    ).length;

    // Use container_assigned_at timestamp to determine assignment timing
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // Count devices with container_number assigned today
    const assignedTodayDevices = devices.filter(d => {
      if (!d.container_assigned_at || !d.container_number || d.container_number.trim() === '') {
        return false;
      }
      const assignedDate = new Date(d.container_assigned_at);
      assignedDate.setHours(0, 0, 0, 0);
      return assignedDate.getTime() === today.getTime();
    });
    const assignedToday = assignedTodayDevices.length;

    // Count devices with container_number assigned in last 7 days
    const assignedLast7DaysDevices = devices.filter(d => {
      if (!d.container_assigned_at || !d.container_number || d.container_number.trim() === '') {
        return false;
      }
      const assignedDate = new Date(d.container_assigned_at);
      assignedDate.setHours(0, 0, 0, 0);
      return assignedDate >= sevenDaysAgo && assignedDate <= today;
    });
    const assignedLast7Days = assignedLast7DaysDevices.length;

    // Percentage in use (within date range)
    const totalInRange = dateFilteredDevices.length;
    const inUseInRange = dateFilteredDevices.filter(
      d => d.container_number && d.container_number.trim() !== ''
    ).length;
    const percentageInUse = totalInRange > 0 ? Math.round((inUseInRange / totalInRange) * 100) : 0;

    setMetrics({
      availableSeals,
      assignedToday,
      assignedLast7Days,
      percentageInUse,
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(v => v !== '').length;
  };

  if (loading) {
    return <div className="operador-dashboard loading">Loading dashboard...</div>;
  }

  return (
    <div className={`operador-dashboard ${showFiltersPanel ? 'panel-open' : ''}`}>
      {/* Settings Button - Outside Container */}
      <div className="dashboard-header">
        <button 
          className="settings-btn"
          onClick={() => fetchInitialData()}
          title="Refresh data"
        >
          🔄
        </button>
        <button 
          className="settings-btn"
          onClick={() => setShowFiltersPanel(!showFiltersPanel)}
          title="Open filters"
        >
          ☰
          {getActiveFilterCount() > 0 && (
            <span className="filter-badge">{getActiveFilterCount()}</span>
          )}
        </button>
      </div>

      {/* Filters Side Panel */}
      <div className={`filters-panel ${showFiltersPanel ? 'open' : ''}`}>
        <div className="filters-panel-header">
          <h2>Filters</h2>
          <button 
            className="close-btn"
            onClick={() => setShowFiltersPanel(false)}
            title="Close filters"
          >
            ✕
          </button>
        </div>
        
        <div className="filters-panel-content">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>

          <button 
            className="btn-reset-panel"
            onClick={resetFilters}
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className="container">
        {/* Depot Header */}
        <div className="depot-header">
          <h1>{depot?.name || 'Depot'}</h1>
          <div className="depot-info">
            <span className="info-badge">{depot?.ruc}</span>
            <span className="info-item">📍 {depot?.address}</span>
            <span className="info-item">📞 {depot?.phone}</span>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-cards">
          <div className="kpi-card">
            <div className="kpi-label">Available Seals<span className="filter-note">(Filterable)</span></div>
            <div className="kpi-value">{metrics.availableSeals.toLocaleString()}</div>
            <div className="kpi-description">Haven't been used by this depot</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-label">Assigned Today</div>
            <div className="kpi-value">{metrics.assignedToday.toLocaleString()}</div>
            <div className="kpi-description">Assigned to containers today</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-label">Assigned Last 7 Days</div>
            <div className="kpi-value">{metrics.assignedLast7Days.toLocaleString()}</div>
            <div className="kpi-description">Assigned to containers (7 days)</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-label">In Use %<span className="filter-note">(Filterable)</span></div>
            <div className="kpi-value">{metrics.percentageInUse}%</div>
            <div className="kpi-description">Of total seals in depot</div>
          </div>
        </div>
      </div>
    </div>
  );
}
